import axios from 'axios';

// TypeScript interfaces for API responses
/**
 * @typedef {Object} CourtType
 * @property {string} id - Unique identifier for the court type
 * @property {string} name - English name of the court type
 * @property {string} thaiName - Thai name of the court type
 * @property {number} index - Display order index
 * @property {boolean} active - Whether the court type is active
 * @property {Object} createdAt - Creation timestamp
 * @property {number} createdAt._seconds - Seconds since epoch
 * @property {number} createdAt._nanoseconds - Nanoseconds
 */

/**
 * @typedef {Object} OpeningHours
 * @property {string} open - Opening time in HH:MM format
 * @property {string} close - Closing time in HH:MM format
 */

/**
 * @typedef {Object} Court
 * @property {string} id - Unique identifier for the court
 * @property {string} name - English name of the court
 * @property {string} type - Court type (e.g., "basketball", "football")
 * @property {string} thaiName - Thai name of the court
 * @property {boolean} active - Whether the court is active
 * @property {number} costPerSlot - Cost per 30-minute slot in THB
 * @property {OpeningHours} openingHours - Court operating hours
 * @property {string[]} bookedSlots - Array of booked time slots in HH:MM format
 */

/**
 * @typedef {Object} CourtAvailabilityResponse
 * @property {string} date - Date in YYYY-MM-DD format
 * @property {string} dayOfWeek - Day of the week (e.g., "thursday")
 * @property {Court[]} courts - Array of courts with availability data
 */

/**
 * @typedef {Object} BookingResponse
 * @property {string} bookingId - Unique booking identifier
 * @property {string} lineUserId - LINE user ID
 * @property {string} displayName - User display name
 * @property {string} courtId - Court ID
 * @property {string} date - Booking date in YYYY-MM-DD format
 * @property {string} startTime - Start time in HH:MM format
 * @property {string} endTime - End time in HH:MM format
 * @property {number} duration - Duration in minutes
 * @property {string} status - Booking status ("pending", "confirmed", "cancelled")
 * @property {string} paymentStatus - Payment status ("pending", "paid", "failed")
 * @property {string} paymentMethod - Payment method ("qr", "tokens")
 * @property {string} courtName - Name of the court
 * @property {number} costPerSlot - Cost per 30-minute slot in THB
 * @property {number} numberOfSlots - Number of 30-minute slots
 * @property {number} totalCost - Total cost in THB
 * @property {Object} createdAt - Creation timestamp
 * @property {string} [paymentId] - Payment ID (for QR payments)
 * @property {string} [qrCode] - QR code for payment (for QR payments)
 * @property {number} [amount] - Payment amount (for QR payments)
 * @property {number} [tokensUsed] - Tokens used (for token payments)
 * @property {number} [remainingTokens] - Remaining tokens (for token payments)
 * @property {string} [courtTypeId] - Court type ID (for token payments)
 * @property {number} [hours] - Hours booked (for token payments)
 */

/**
 * @typedef {Object} PaymentResponse
 * @property {string} paymentId - Unique payment identifier
 * @property {string} qrCode - Base64 encoded QR code image
 * @property {number} amount - Payment amount in THB
 * @property {string} status - Payment status ("pending", "completed", "failed")
 * @property {Object} booking - Associated booking information
 * @property {string} booking.id - Booking ID
 * @property {string} booking.lineUserId - LINE user ID
 * @property {string} booking.displayName - User display name
 * @property {string} booking.courtId - Court ID
 * @property {string} booking.date - Booking date in YYYY-MM-DD format
 * @property {string} booking.startTime - Start time in HH:MM format
 * @property {string} booking.endTime - End time in HH:MM format
 * @property {number} booking.duration - Duration in minutes
 * @property {string} booking.status - Booking status
 * @property {string} booking.paymentStatus - Payment status
 * @property {Object} booking.createdAt - Creation timestamp
 * @property {string} courtName - Name of the court
 * @property {Object} createdAt - Payment creation timestamp
 */

/**
 * @typedef {Object} BookingHistory
 * @property {string} id - Booking ID
 * @property {string} date - Booking date in YYYY-MM-DD format
 * @property {string} startTime - Start time in HH:MM format
 * @property {string} endTime - End time in HH:MM format
 * @property {number} duration - Duration in minutes
 * @property {string} status - Booking status ("confirmed", "cancelled", etc.)
 * @property {string} paymentStatus - Payment status ("paid", "pending", "failed")
 * @property {Object} createdAt - Creation timestamp
 * @property {number} createdAt._seconds - Seconds since epoch
 * @property {number} createdAt._nanoseconds - Nanoseconds
 * @property {string} courtId - Court ID
 * @property {string} userId - User ID
 * @property {string} courtName - Name of the court
 * @property {Object|null} payment - Payment information or null
 * @property {string} [payment.paymentMethod] - Payment method ("qr", "tokens")
 * @property {number} [payment.tokensUsed] - Tokens used (for token payments)
 * @property {string} [payment.courtTypeId] - Court type ID (for token payments)
 * @property {number} [payment.amount] - Payment amount (for QR payments)
 */

/**
 * @typedef {Object} UserProfile
 * @property {string} id - User ID from database
 * @property {string} lineUserId - LINE user ID
 * @property {string} displayName - User display name
 * @property {string} picture - User profile picture URL
 * @property {number} points - Legacy user points (deprecated)
 * @property {Object} tokens - Court-type specific tokens
 * @property {number} tokens.football - Football court tokens
 * @property {number} tokens.basketball - Basketball court tokens
 * @property {number} tokens.tennis - Tennis court tokens
 * @property {number} tokens.badminton - Badminton court tokens
 * @property {number} tokens.pickleball - Pickleball court tokens
 * @property {number} tokens.studio - Studio tokens
 * @property {Object} createdAt - Creation timestamp
 * @property {number} createdAt._seconds - Seconds since epoch
 * @property {number} createdAt._nanoseconds - Nanoseconds
 * @property {Object} updatedAt - Last update timestamp
 * @property {number} updatedAt._seconds - Seconds since epoch
 * @property {number} updatedAt._nanoseconds - Nanoseconds
 * @property {BookingHistory[]} bookings - Array of user's booking history
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5001/fc-court-booking-api/us-central1/api';

// Helper function to convert Firestore timestamp to Date
const convertFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a Date object, return it
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a string, try to parse it
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  // If it's a Firestore timestamp object with _seconds
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }
  
  // If it's a number (milliseconds), convert to Date
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  
  return null;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);
    if (error.response) {
      console.error('Error data:', error.response.data);
      console.error('Error status:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Token refresh wrapper function
const withTokenRefresh = async (apiCall, liff) => {
  try {
    return await apiCall();
  } catch (error) {
    // Check if it's a token expired error
    if (error.message === 'Token expired' || 
        error.message?.includes('Token expired') ||
        error.response?.data?.error === 'Token expired' ||
        error.response?.data?.error_description === 'IdToken expired.') {
      console.log('Token expired, logging out and logging in again...');
      await liff.logout();
      await liff.login();
      // Page will redirect, so we don't need to return anything
      return;
    }
    throw error;
  }
};

// API functions
export const courtApi = {
  /**
   * Get user profile using LINE ID token
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} liff - LIFF instance for token refresh
   * @returns {Promise<UserProfile>} User profile data
   */
  getUserProfile: async (idToken, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.get(`/user/profile?idToken=${idToken}`);
        const data = response.data;
        
        // Process the response to handle new timestamp format and ensure bookings array exists
        const processedData = {
          ...data,
          createdAt: convertFirestoreTimestamp(data.createdAt),
          updatedAt: convertFirestoreTimestamp(data.updatedAt),
          bookings: data.bookings || []
        };
        
        // Process booking timestamps
        if (processedData.bookings) {
          processedData.bookings = processedData.bookings.map(booking => ({
            ...booking,
            createdAt: convertFirestoreTimestamp(booking.createdAt)
          }));
        }
        
        return processedData;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error('Failed to fetch user profile');
      }
    }, liff);
  },

  /**
   * Get a page of the user's booking history (infinite scroll).
   * @param {string} idToken - LINE ID token
   * @param {Object} opts - { cursor, limit }
   * @param {Object} liff - LIFF instance for token refresh
   * @returns {Promise<{bookings: Array, nextCursor: (string|null), hasMore: boolean}>}
   */
  getUserBookings: async (idToken, { cursor = null, limit = 10 } = {}, liff) => {
    return withTokenRefresh(async () => {
      try {
        const params = new URLSearchParams({ idToken, limit: String(limit) });
        if (cursor) params.set('cursor', cursor);
        const response = await api.get(`/user/bookings?${params.toString()}`);
        const data = response.data;
        const bookings = (data.bookings || []).map((b) => ({
          ...b,
          createdAt: convertFirestoreTimestamp(b.createdAt),
        }));
        return { bookings, nextCursor: data.nextCursor ?? null, hasMore: !!data.hasMore };
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error('Failed to fetch booking history');
      }
    }, liff);
  },

  /**
   * List active credit packs for a court type (customer buy sheet, F2).
   */
  getCreditPacks: async (courtTypeId) => {
    try {
      const q = courtTypeId ? `?courtTypeId=${courtTypeId}` : '';
      const response = await api.get(`/courts/credit-packs${q}`);
      return response.data || [];
    } catch (error) {
      throw new Error('Failed to load credit packs');
    }
  },

  /**
   * Buy a credit pack — creates a topup + ChillPay payment; returns paymentUrl to redirect to.
   */
  createTopup: async (idToken, packId, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.post('/payments/topup/create', { idToken, packId });
        return response.data;
      } catch (error) {
        if (error.response?.data?.error) throw new Error(error.response.data.error);
        throw new Error('Failed to start purchase');
      }
    }, liff);
  },

  /**
   * Self-cancel a future, confirmed, token-paid booking (free up to the monthly quota).
   * @param {string} idToken
   * @param {string} bookingId
   * @param {Object} liff
   * @returns {Promise<{success:boolean, refundedTokens:number, courtTypeId:string, newBalance:number, remainingFreeCancels:number}>}
   */
  selfCancelBooking: async (idToken, bookingId, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.post(`/bookings/${bookingId}/self-cancel`, { idToken });
        return response.data;
      } catch (error) {
        const data = error.response?.data;
        if (data?.error) {
          const err = new Error(data.error);
          err.code = data.code;
          throw err;
        }
        throw new Error('Failed to cancel booking');
      }
    }, liff);
  },

  /**
   * Get a fresh ChillPay QR link for a pending booking. ChillPay links are
   * single-use, so reopening from history must create a new one.
   * @param {string} idToken
   * @param {string} bookingId
   * @param {Object} liff
   * @returns {Promise<{status:'pending'|'paid', paymentId:string, paymentUrl?:string, holdUntil?:string}>}
   */
  getPaymentLink: async (idToken, bookingId, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.post(`/bookings/${bookingId}/payment-link`, { idToken }, { timeout: 30000 });
        return response.data;
      } catch (error) {
        const data = error.response?.data;
        if (data?.error) {
          const err = new Error(data.error);
          err.code = data.code;
          throw err;
        }
        throw new Error('Failed to create payment link');
      }
    }, liff);
  },

  /**
   * Get all court types
   * @returns {Promise<CourtType[]>} Array of court types sorted by index
   */
  getCourtTypes: async () => {
    try {
      const response = await api.get('/courts/types');
      // Sort by index numerically and filter only active court types
      const sortedData = response.data
        .filter(court => court.active === true)
        .sort((a, b) => a.index - b.index);
      return sortedData;
    } catch (error) {
      throw new Error('Failed to fetch court types');
    }
  },

  /**
   * Get court availability for a specific date
   * @param {string} courtTypeId - Court type ID
   * @param {Date} date - Date to check availability for
   * @returns {Promise<CourtAvailabilityResponse>} Court availability data
   */
  getCourtAvailability: async (courtTypeId, date) => {
    try {
      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      const response = await api.get(`/courts/type/${courtTypeId}/available?date=${formattedDate}`);
      return response.data;
    } catch (error) {
      console.error('API ERR:', error);
      throw new Error(`Failed to fetch court availability`);
    }
  },

  /**
   * Get court availability for a date range (batch)
   * @param {string} courtTypeId - Court type ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Object>} Court availability data for the date range
   * @returns {Object.<string, CourtAvailabilityResponse>} Key-value pairs of date -> availability data
   */
  getCourtAvailabilityRange: async (courtTypeId, startDate, endDate) => {
    try {
      const response = await api.get(`/courts/type/${courtTypeId}/available/range?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error('API ERR:', error);
      throw new Error(`Failed to fetch court availability range`);
    }
  },

  /**
   * Create a new booking
   * @param {Object} bookingData - Booking data
   * @param {string} bookingData.idToken - LINE ID token for authentication
   * @param {string} bookingData.courtId - Court ID
   * @param {string} bookingData.date - Date in YYYY-MM-DD format
   * @param {string} bookingData.startTime - Start time in HH:MM format
   * @param {number} bookingData.duration - Duration in minutes
   * @param {boolean} bookingData.usePoints - Whether to use points for payment
   * @param {boolean} bookingData.useTokens - Whether to use tokens for payment
   * @param {Object} [bookingData.contactData] - Contact information
   * @param {string} [bookingData.contactData.nickname] - User's nickname
   * @param {string} [bookingData.contactData.mobilePhone] - User's mobile phone
   * @param {Object} liff - LIFF instance for token refresh
   * @returns {Promise<BookingResponse>} Booking response data
   */
  createBooking: async (bookingData, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.post('/bookings', bookingData);
        return response.data;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error('Failed to create booking');
      }
    }, liff);
  },

  /**
   * Get payment information by payment ID
   * @param {string} paymentId - Payment ID
   * @returns {Promise<PaymentResponse>} Payment data with QR code and booking info
   */
  getPayment: async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to fetch payment');
    }
  },

  /**
   * Get court type rules
   * @param {string} courtTypeId - Court type ID
   * @returns {Promise<Object>} Rules object with thai and english HTML content
   */
  getCourtTypeRules: async (courtTypeId) => {
    try {
      const response = await api.get(`/courts/types/${courtTypeId}/rules`);
      return response.data.rules || { thai: '', english: '' };
    } catch (error) {
      console.error('Error fetching court type rules:', error);
      return { thai: '', english: '' }; // Return empty rules if not available
    }
  },

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID to cancel
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} liff - LIFF instance for token refresh
   * @returns {Promise<Object>} Cancellation result
   */
  cancelBooking: async (bookingId, idToken, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.delete(`/bookings/${bookingId}?idToken=${idToken}`);
        return response.data;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error('Failed to cancel booking');
      }
    }, liff);
  },

  /**
   * Update user profile with contact information
   * @param {string} idToken - LINE ID token for authentication
   * @param {Object} contactData - Contact information to update
   * @param {string} contactData.nickname - User's nickname
   * @param {string} contactData.mobilePhone - User's mobile phone number
   * @param {Object} liff - LIFF instance for token refresh
   * @returns {Promise<Object>} Updated user profile
   */
  updateUserContact: async (idToken, contactData, liff) => {
    return withTokenRefresh(async () => {
      try {
        const response = await api.put('/user/profile', {
          idToken,
          ...contactData
        });
        return response.data;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error('Failed to update user contact information');
      }
    }, liff);
  },
};

export default api; 